import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Divider,
    Checkbox,
    FormControlLabel,
    TextField,
    CircularProgress,
    Alert,
    Avatar,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const DeliveryConfirmation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    // Form states
    const [isClientPickup, setIsClientPickup] = useState(true);
    const [receiverName, setReceiverName] = useState('');
    const [deliveryNotes, setDeliveryNotes] = useState('');

    const fetchOrder = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/orders/${id}/`);
            setOrder(response.data);
            // Initialize states from order if already set
            if (response.data.receiver_name) setReceiverName(response.data.receiver_name);
            setIsClientPickup(response.data.is_client_pickup !== false);
            if (response.data.delivery_notes) setDeliveryNotes(response.data.delivery_notes);
        } catch (error) {
            console.error('Error fetching order:', error);
            setError('Error al cargar los datos del pedido');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    const handleConfirmDelivery = async () => {
        if (!isClientPickup && !receiverName.trim()) {
            setError('Debe ingresar el nombre de la persona que retira');
            return;
        }

        setSaving(true);
        try {
            await axios.patch(`/api/orders/${id}/`, {
                status: 'entregado',
                receiver_name: isClientPickup ? (order.client?.name || 'Cliente') : receiverName,
                is_client_pickup: isClientPickup,
                delivery_notes: deliveryNotes,
                delivered_at: new Date().toISOString()
            });
            setSuccess('¡Pedido entregado con éxito!');
            setTimeout(() => {
                navigate(ordersListPath);
            }, 2000);
        } catch (error) {
            setError('Error al confirmar la entrega');
        } finally {
            setSaving(false);
        }
    };

    // Las entregas se inician desde el listado de pedidos de cada canal
    const ordersListPath = order?.order_type === 'wholesale' ? '/orders/business/list' : '/orders/public/list';

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Sidebar onLogout={handleLogout} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (!order) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Sidebar onLogout={handleLogout} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Alert severity="error">Pedido no encontrado</Alert>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
            <Sidebar onLogout={handleLogout} />
            <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, pt: { xs: 7, md: 3 } }}>
                {/* Breadcrumb / Back */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(ordersListPath)}
                        sx={{ color: '#4e7397', textTransform: 'none', '&:hover': { bgcolor: 'transparent', color: '#1976d2' } }}
                    >
                        Volver a Pedidos
                    </Button>
                </Box>

                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography variant="h4" sx={{ mb: 1, color: '#0e141b', fontWeight: 900, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                        Confirmar Entrega
                    </Typography>
                    <Typography sx={{ mb: 4, color: '#4e7397', fontSize: '0.875rem' }}>
                        Verifique los datos y registre quién retira el pedido.
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

                    <Grid container spacing={3}>
                        {/* Client Info */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1976d2' }}><PersonIcon /></Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Datos del Cliente</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>{order.client?.name || 'Consumidor Final'}</Typography>
                                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>DNI/CUIT: {order.client?.cuit || 'N/A'}</Typography>
                                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', mt: 1 }}>Tel: {order.client?.phone || 'N/A'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Order Info */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#800020' }}><ReceiptIcon /></Avatar>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Pedido #{order.order_number}</Typography>
                                            <Chip
                                                label={order.order_type === 'wholesale' ? 'Negocio' : 'Público'}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: order.order_type === 'wholesale' ? '#ede7f6' : '#e0f7fa',
                                                    color: order.order_type === 'wholesale' ? '#5e35b1' : '#00838f',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography sx={{ color: '#4e7397' }}>Productos:</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>{order.item_count}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography sx={{ color: '#4e7397' }}>Peso Total:</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>{order.total_weight} kg</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ color: '#4e7397' }}>Precio Total:</Typography>
                                        <Typography sx={{ fontWeight: 800, color: '#1b5e20' }}>${parseFloat(order.total).toLocaleString('es-AR')}</Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Delivery Form */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: 4, border: '1px solid #d0dbe7', borderRadius: '0.75rem', boxShadow: 'none' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                    <Avatar sx={{ bgcolor: '#2e7d32' }}><CheckCircleIcon /></Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Registro de Entrega</Typography>
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isClientPickup}
                                                onChange={(e) => {
                                                    setIsClientPickup(e.target.checked);
                                                    if (e.target.checked) setReceiverName('');
                                                }}
                                                sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                                            />
                                        }
                                        label={<Typography sx={{ fontWeight: 500 }}>¿El cliente retira personalmente?</Typography>}
                                    />

                                    {!isClientPickup && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f8e9', borderRadius: '0.5rem', border: '1px solid #c8e6c9' }}>
                                            <Typography sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 600, color: '#2e7d32' }}>
                                                Datos de quien retira:
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                placeholder="Nombre completo y DNI de quien retira"
                                                value={receiverName}
                                                onChange={(e) => setReceiverName(e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                sx={{ bgcolor: 'white' }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notas de entrega (Opcional)"
                                    value={deliveryNotes}
                                    onChange={(e) => setDeliveryNotes(e.target.value)}
                                    placeholder="Ej: Se entrega en caja cerrada, el cliente verificó mercadería..."
                                    sx={{ mb: 4 }}
                                />

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth={{ xs: true, sm: false }}
                                        onClick={() => navigate(ordersListPath)}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth={{ xs: true, sm: false }}
                                        disabled={saving}
                                        onClick={handleConfirmDelivery}
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                        sx={{
                                            bgcolor: '#2e7d32',
                                            '&:hover': { bgcolor: '#1b5e20' },
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            px: 4
                                        }}
                                    >
                                        Confirmar y Finalizar
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default DeliveryConfirmation;
